import { Link } from "./router";
import type { SessionResource } from "./useSessionResources";

const SLOT_ORDER: readonly SessionResource["slot"][] = ["handout", "slides", "links", "extra"];

const SLOT_LABELS: Record<SessionResource["slot"], string> = {
  handout: "Handout",
  slides: "Slides",
  links: "Links and resources",
  extra: "Extra reading",
};

const MEDIA_LABELS: Record<string, string> = {
  "application/pdf": "PDF",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PowerPoint",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel",
  "image/jpeg": "Image",
  "image/png": "Image",
  "image/webp": "Image",
  "text/csv": "Spreadsheet",
  "text/plain": "Text",
};

/**
 * Material for one session. The handout comes first because it is the thing
 * somebody wants during the session, not because it was submitted first.
 *
 * Every row says what happens before it is tapped. An address that opens in the
 * app, a file that downloads, and something that is not available yet all read
 * differently on purpose.
 */
export function SessionResources({ resources, signedIn, eventSlug, embedded = false }: {
  resources: readonly SessionResource[];
  signedIn: boolean;
  eventSlug: string;
  embedded?: boolean;
}) {
  if (resources.length === 0) return null;
  const grouped = SLOT_ORDER
    .map((slot) => ({ slot, items: resources.filter((entry) => entry.slot === slot) }))
    .filter((group) => group.items.length > 0);

  return (
    <section className={`detail-section resource-section${embedded ? " embedded" : ""}`} aria-label={embedded ? "Presenter materials" : undefined} aria-labelledby={embedded ? undefined : "session-materials-title"}>
      {!embedded && <h2 id="session-materials-title">Materials</h2>}
      {!signedIn && (
        <p className="resource-signin-note">
          <Link href="/account">Sign in</Link> to open these on this device.
        </p>
      )}
      {grouped.map((group) => (
        <div className="resource-group" key={group.slot}>
          <h3>{SLOT_LABELS[group.slot]}</h3>
          <ul className="resource-list">
            {group.items.map((entry, index) => (
              <li key={entry.id ?? `${group.slot}-${index}`}>
                <ResourceRow resource={entry} signedIn={signedIn} eventSlug={eventSlug} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

function ResourceRow({ resource, signedIn, eventSlug }: { resource: SessionResource; signedIn: boolean; eventSlug: string }) {
  const detail = describe(resource);

  if (!resource.open) {
    return (
      <div className="resource-row waiting">
        <span className="resource-title">{resource.title}</span>
        <span className="resource-detail">Available {formatOpensAt(resource.opensAt)}</span>
      </div>
    );
  }

  if (!signedIn) {
    return (
      <Link className="resource-row locked" href="/account">
        <span className="resource-title">{resource.title}</span>
        <span className="resource-detail">{detail} · Sign in to open</span>
        <b aria-hidden="true">›</b>
      </Link>
    );
  }

  if (resource.kind === "link" && resource.url) {
    return (
      <a className="resource-row" href={resource.url} target="_blank" rel="noreferrer noopener">
        <span className="resource-title">{resource.title}</span>
        <span className="resource-detail">{hostOf(resource.url)} · Opens in a new tab</span>
        <b aria-hidden="true">↗</b>
      </a>
    );
  }

  if (resource.id && resource.convertedFile) {
    return (
      <div className="resource-row resource-row-with-actions">
        <span className="resource-title">{resource.title}</span>
        <span className="resource-detail">{detail}</span>
        <span className="resource-actions">
          <a href={resource.convertedFile.url} target="_blank" rel="noreferrer noopener">Open PDF</a>
          <a
            href={`/api/attendee/events/${encodeURIComponent(eventSlug)}/resources/${encodeURIComponent(resource.id)}/file`}
            download={resource.fileName ?? true}
          >Download PowerPoint</a>
        </span>
      </div>
    );
  }

  if (resource.id) {
    const opensHere = resource.mediaType === "application/pdf";
    return (
      <a
        className="resource-row"
        href={`/api/attendee/events/${encodeURIComponent(eventSlug)}/resources/${encodeURIComponent(resource.id)}/file`}
        {...(opensHere ? { target: "_blank", rel: "noreferrer noopener" } : { download: resource.fileName ?? true })}
      >
        <span className="resource-title">{resource.title}</span>
        <span className="resource-detail">{detail} · {opensHere ? "Opens here" : "Downloads"}</span>
        <b aria-hidden="true">{opensHere ? "↗" : "↓"}</b>
      </a>
    );
  }

  return (
    <div className="resource-row waiting">
      <span className="resource-title">{resource.title}</span>
      <span className="resource-detail">{detail}</span>
    </div>
  );
}

function describe(resource: SessionResource): string {
  if (resource.kind === "link") return resource.url ? hostOf(resource.url) : "Link";
  const type = resource.mediaType ? MEDIA_LABELS[resource.mediaType] ?? "File" : "File";
  return resource.sizeBytes ? `${type} · ${formatSize(resource.sizeBytes)}` : type;
}

function hostOf(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./u, "");
  } catch {
    return "Link";
  }
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

/** `opensAt` is wall-clock in the event's own zone, so it is read, not converted. */
function formatOpensAt(opensAt: string): string {
  const match = opensAt.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/u);
  if (!match) return "when the session starts";
  const [, year, month, day, hour, minute] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  const weekday = new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(date);
  const hours = Number(hour);
  const suffix = hours < 12 ? "a.m." : "p.m.";
  const twelve = hours % 12 === 0 ? 12 : hours % 12;
  return `${weekday} at ${twelve}:${minute} ${suffix}`;
}
